/**
 * Company module constants
 * ค่าคงที่ของโมดูลบริษัท
 */

/**
 * Formal fields that require evidence when changed
 * ฟิลด์ที่ต้องมีหลักฐานเมื่อแก้ไข (มีเครื่องหมาย ◆)
 */
export const FORMAL_FIELDS = [
  'nameTh',
  'nameEn',
  'registrationNo',
  'registeredCapital',
  'businessType',
  'directors',
  'address',
] as const

export type FormalField = (typeof FORMAL_FIELDS)[number]

/**
 * Status filter options
 * ตัวเลือกการกรองตามสถานะ
 */
export const STATUS_OPTIONS = [
  { value: 'all', labelKey: 'status.all' },
  { value: 'in-progress', labelKey: 'status.inProgress' },
  { value: 'completed', labelKey: 'status.completed' },
  { value: 'not-started', labelKey: 'status.notStarted' },
] as const

/**
 * Document source options
 * ตัวเลือกที่มาของเอกสาร
 */
export const DOCUMENT_SOURCE_OPTIONS = [
  { value: 'all', labelKey: 'companies.documents.all' },
  { value: 'initial', labelKey: 'companies.documents.initial' },
  { value: 'change', labelKey: 'companies.documents.change' },
] as const

/**
 * Allowed file types for upload
 * ประเภทไฟล์ที่อนุญาตให้อัปโหลด
 */
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const

/**
 * Maximum file size in bytes (10MB)
 * ขนาดไฟล์สูงสุด (ไบต์)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024

/**
 * Default pagination settings
 * การตั้งค่าการแบ่งหน้าเริ่มต้น
 */
export const DEFAULT_PAGE_SIZE = 10
export const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

/**
 * Company form field definitions for UI
 * การกำหนดฟิลด์ฟอร์มบริษัทสำหรับ UI
 */
export const COMPANY_FORM_FIELDS = {
  registration: [
    { name: 'nameTh', labelKey: 'companies.fields.nameTh', isFormal: true, required: true },
    { name: 'nameEn', labelKey: 'companies.fields.nameEn', isFormal: true, required: true },
    {
      name: 'registrationNo',
      labelKey: 'companies.fields.registrationNo',
      isFormal: true,
      required: true,
    },
    { name: 'taxId', labelKey: 'companies.fields.taxId', isFormal: false, required: true },
    {
      name: 'registeredCapital',
      labelKey: 'companies.fields.registeredCapital',
      isFormal: true,
      required: true,
    },
    {
      name: 'businessType',
      labelKey: 'companies.fields.businessType',
      isFormal: true,
      required: true,
    },
    {
      name: 'directors',
      labelKey: 'companies.fields.directors',
      isFormal: true,
      required: true,
      multiline: true,
    },
    {
      name: 'address',
      labelKey: 'companies.fields.address',
      isFormal: true,
      required: true,
      multiline: true,
    },
  ],
  contact: [
    { name: 'phone', labelKey: 'companies.fields.phone', isFormal: false, required: false },
    { name: 'email', labelKey: 'companies.fields.email', isFormal: false, required: false },
    {
      name: 'contactPerson',
      labelKey: 'companies.fields.contactPerson',
      isFormal: false,
      required: false,
    },
    {
      name: 'contactPhone',
      labelKey: 'companies.fields.contactPhone',
      isFormal: false,
      required: false,
    },
  ],
} as const

/**
 * Validation error keys
 * คีย์ข้อความผิดพลาดการตรวจสอบ
 */
export const VALIDATION_ERROR_KEYS = {
  required: 'companies.errors.required',
  taxIdFormat: 'companies.errors.taxIdFormat',
  emailFormat: 'companies.errors.emailFormat',
  evidenceRequired: 'companies.errors.evidenceRequired',
  fileType: 'companies.errors.fileType',
  fileSize: 'companies.errors.fileSize',
} as const
